import { DocumentUpload } from '@wheely/ui-kit';
import cn from 'classnames';
import React, { useCallback, useRef } from 'react';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';

import { IDocUploaderForm } from '@/shared/store/doc-uploader/types';
import { isNewFile, needsNewFile } from '@/shared/store/doc-uploader/upload-form';
import { noop } from '@/shared/utils/noop';
import { Banner } from '@/shared/view/Banner';
import { DocStatusBadge } from '@/shared/view/DocStatusBadge';
import { Button } from '@src-common/view/adapters/Button';

import s from './styles.module.css';

type TOnChangeValue<TDocType> = <K extends keyof IDocUploaderForm<TDocType>>(
  _: any,
  { value, name }: { value: IDocUploaderForm<TDocType>[K]; name: K },
) => void;

interface IDocUploadBlockProps<TDocType> {
  title: React.ReactNode;
  description?: string;
  form: IDocUploaderForm<TDocType>;
  onChangeValue: TOnChangeValue<TDocType>;
  isUploading: boolean;
  hideStatus?: boolean;
  accept?: string;
  fileTypes?: string[];
  error?: string;
  aside?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  testIdPrefix?: string;
  variant?: 'main' | 'nested';
}

const BYTES_IN_KB = 1024;
const BYTES_IN_MB = BYTES_IN_KB * 1024;

const formatFileSize = (intl: IntlShape, bytes: number) =>
  bytes >= BYTES_IN_MB
    ? intl.formatNumber(bytes / BYTES_IN_MB, {
        style: 'unit',
        unit: 'megabyte',
        maximumFractionDigits: 1,
      })
    : intl.formatNumber(Math.max(1, Math.round(bytes / BYTES_IN_KB)), {
        style: 'unit',
        unit: 'kilobyte',
      });

const getRejectionText = <TDocType,>(intl: IntlShape, form: IDocUploaderForm<TDocType>) => {
  if (form.rejectReason) {
    return form.rejectReason;
  }

  if (form.status === 'rejected') {
    return intl.formatMessage({
      defaultMessage: 'Rejected',
      id: 'pibz1z',
      description: 'Upload form: error document rejected',
    });
  }

  if (form.status === 'expired') {
    return intl.formatMessage({
      defaultMessage: 'Expired',
      id: 'LJVq4Q',
      description: 'Upload form: error document expired',
    });
  }

  return undefined;
};

const DocUploadBlock = <TDocType extends string>({
  title,
  description,
  form,
  onChangeValue,
  isUploading,
  hideStatus = false,
  accept,
  fileTypes = ['jpeg', 'png', 'pdf'],
  error,
  aside,
  children,
  className,
  testIdPrefix = 'dialog',
  variant = 'main',
}: IDocUploadBlockProps<TDocType>) => {
  const intl = useIntl();
  const uploadRef = useRef<HTMLDivElement>(null);

  const newFile = isNewFile(form) ? form.file?.file : undefined;
  const isNewFileNeeded = needsNewFile(form);
  const bannerText = error || getRejectionText(intl, form);

  const handleUploadAgain = useCallback(() => {
    uploadRef.current?.querySelector<HTMLInputElement>('input[type="file"]')?.click();
  }, []);

  return (
    <div
      className={cn(s.root, s[variant], { [s.needsNewFile]: isNewFileNeeded }, className)}
      data-test-id={`${testIdPrefix}__doc-block`}
    >
      <div className={s.header}>
        {title}
        {form.status && !hideStatus && <DocStatusBadge status={form.status} />}
      </div>
      {description && (
        <div
          className={s.desc}
          dangerouslySetInnerHTML={{ __html: description }}
          data-test-id={`${testIdPrefix}__description`}
        />
      )}
      {bannerText && (
        <Banner
          intent="danger"
          className={s.banner}
          dataTestId={`${testIdPrefix}__${error ? 'upload-error' : 'rejection-banner'}`}
        >
          {bannerText}
        </Banner>
      )}
      <div className={s.media}>
        <div className={s.fileColumn}>
          <div
            ref={uploadRef}
            className={s.file}
            data-test-id={`${testIdPrefix}__upload-input-wrapper`}
          >
            <DocumentUpload
              name="file"
              value={form.file}
              accept={accept}
              onChange={isUploading ? noop : (onChangeValue as any)}
            />
          </div>
          {newFile && (
            <div className={s.fileCaption} data-test-id={`${testIdPrefix}__file-caption`}>
              {newFile.name} · {formatFileSize(intl, newFile.size)}
            </div>
          )}
          {!form.file && (
            <div className={s.fileCaption} data-test-id={`${testIdPrefix}__file-types`}>
              <FormattedMessage
                defaultMessage="Add {fileTypes}"
                id="avn0lI"
                description="Upload view: add file types"
                values={{ fileTypes: intl.formatList(fileTypes, { type: 'disjunction' }) }}
              />
            </div>
          )}
        </div>
        {aside && <div className={s.aside}>{aside}</div>}
      </div>
      {children}
      {isNewFileNeeded && (
        <Button
          className={s.uploadAgain}
          onClick={handleUploadAgain}
          isDisabled={isUploading}
          data-test-id={`${testIdPrefix}__upload-again-button`}
        >
          {intl.formatMessage({
            defaultMessage: 'Upload again',
            id: 'upload_document_upload_again',
            description:
              'Upload form: button to pick a new file for a rejected or expired document',
          })}
        </Button>
      )}
    </div>
  );
};

export type { TOnChangeValue };
export { DocUploadBlock };
